import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';

// Premium Product Field Form - Clean & Sophisticated
function ProductFieldForm() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        field_label: '',
        field_key: '',
        field_type: 'text',
        required: false,
        options: ''
    });
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const fieldTypes = [
        { value: 'text', label: '🔤 Short Text' },
        { value: 'number', label: '🔢 Numeric Value' },
        { value: 'textarea', label: '📝 Long Description' },
        { value: 'select', label: '🔽 Dropdown Menu' },
        { value: 'checkbox', label: '✅ Toggle / Boolean' },
        { value: 'date', label: '📅 Event Date' }
    ];

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        if (name === 'field_label') {
            const key = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
            setFormData(prev => ({ ...prev, field_key: key }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        try {
            const response = await api.post('/settings/product_fields', {
                product_field: formData
            });

            if (response.success) {
                navigate('/settings/product-fields');
            } else {
                setError(response.message || 'System error while registering attribute');
            }
        } catch (err: any) {
            setError(err.message || 'Fatal communication failure');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="text-2xl sm:text-4xl font-black text-slate-900 tracking-tight">
                        Define <span className="text-indigo-600">Custom Attribute</span>
                    </h1>
                    <p className="text-slate-500 mt-1 font-medium italic">Empower your inventory with industry-specific data points</p>
                </div>
                <Link to="/settings/product-fields" className="p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </Link>
            </div>

            <div className="bg-white rounded-lg border border-slate-100 shadow-2xl shadow-slate-200/50 p-8 sm:p-12 transition-all">
                <form onSubmit={handleSubmit} className="space-y-10">
                    {error && (
                        <div className="bg-rose-50 border-l-4 border-rose-500 p-5 rounded-2xl shadow-sm animate-bounce-short">
                            <div className="flex gap-3 items-center">
                                <svg className="w-5 h-5 text-rose-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm font-black text-rose-800">{error}</p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* Primary Identity */}
                        <div className="md:col-span-2 space-y-8">
                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Display Label <span className="text-rose-500">*</span></label>
                                <input
                                    type="text"
                                    name="field_label"
                                    value={formData.field_label}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-900 shadow-sm text-lg"
                                    placeholder="e.g. Battery Cycle, Warranty Period"
                                />
                                <p className="text-[10px] text-slate-400 font-bold italic pl-1">Visible to all users during data entry</p>
                            </div>

                            <div className="space-y-3">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1">Unique Key Identifier</label>
                                <input
                                    type="text"
                                    name="field_key"
                                    value={formData.field_key}
                                    onChange={handleChange}
                                    required
                                    className="w-full px-6 py-4 bg-slate-100/50 border border-slate-50 rounded-2xl outline-none font-mono text-sm text-indigo-600 shadow-inner"
                                    placeholder="auto_generated_key"
                                />
                                <p className="text-[10px] text-slate-400 font-bold italic pl-1">Immutable system ID for database storage</p>
                            </div>
                        </div>

                        {/* Behavior Controls */}
                        <div className="space-y-3">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1 text-indigo-600">Input Signature</label>
                            <select
                                name="field_type"
                                value={formData.field_type}
                                onChange={handleChange}
                                className="w-full px-6 py-5 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:bg-white focus:border-indigo-500 transition-all font-black text-slate-700 shadow-sm appearance-none cursor-pointer"
                            >
                                {fieldTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center pt-8">
                            <label className="flex items-center gap-4 cursor-pointer group">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        name="required"
                                        id="required"
                                        checked={formData.required}
                                        onChange={handleChange}
                                        className="sr-only"
                                    />
                                    <div className={`w-14 h-8 rounded-full transition-colors duration-300 ${formData.required ? 'bg-indigo-600' : 'bg-slate-200'}`}></div>
                                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform duration-300 shadow-sm ${formData.required ? 'translate-x-6' : ''}`}></div>
                                </div>
                                <div>
                                    <p className="text-sm font-black text-slate-900 group-hover:text-indigo-600 transition-colors">Mandatory Field</p>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Enforce data availability</p>
                                </div>
                            </label>
                        </div>

                        {/* Conditional Logic for Select */}
                        {formData.field_type === 'select' && (
                            <div className="md:col-span-2 space-y-3 animate-in slide-in-from-top-4 duration-500">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] pl-1 text-emerald-600">Dataset Options</label>
                                <textarea
                                    name="options"
                                    value={formData.options}
                                    onChange={handleChange}
                                    rows={3}
                                    className="w-full px-6 py-5 bg-emerald-50/30 border border-emerald-100 rounded-3xl outline-none focus:bg-white focus:border-emerald-500 transition-all font-bold text-slate-700 shadow-sm"
                                    placeholder="Option A, Option B, Option C..."
                                />
                                <p className="text-[10px] text-slate-400 font-bold italic pl-1 text-emerald-600">Delimit items using standard commas</p>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-4 pt-10 border-t border-slate-50">
                        <Link
                            to="/settings/product-fields"
                            className="px-8 py-5 bg-white border border-slate-200 text-slate-500 rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] transition-all hover:bg-slate-50 text-center"
                        >
                            Back to Collection
                        </Link>
                        <button
                            type="submit"
                            disabled={saving}
                            className={`px-12 py-5 bg-slate-900 text-white rounded-3xl font-black uppercase tracking-[0.2em] text-[10px] transition-all shadow-2xl shadow-indigo-100 hover:bg-black hover:scale-105 active:scale-95 ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {saving ? 'Registering...' : 'Add Attribute'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ProductFieldForm;
